<template>
  <div class="pb-2">
    <active-filters />
    <b-overlay :show="showOverlay">
      <b-card
        no-body
        no-footer
      >
        <b-card-body class="p-0">
          <b-table-simple
            class="mb-0"
            responsive
            :busy="showOverlay"
          >
            <b-thead>
              <b-tr>
                <b-th
                  class="bg-primary d-flex text-white f-16"
                  sticky-column
                >
                  <span>{{ $t('table.name') }}</span>
                  <table-header-sort
                    sort-label="NAME"
                    :sort-options="sortOptions"
                    @update:sort-options="options => $emit('update:sort-options', options)"
                  />
                </b-th>
                <b-th class="bg-primary text-white f-16">
                  {{ $t('table.total') }}
                </b-th>
                <b-th
                  v-for="wa of weekArray"
                  :key="wa.key"
                  class="bg-primary text-white text-capitalize text-nowrap"
                  :class="{ 'today-column': isToday(wa.key) }"
                >
                  {{ wa.text }}
                </b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr
                v-for="row of rows"
                :key="row.uuid"
                :set="activities = row.dailyActivities.data"
              >
                <b-td
                  class="px-0"
                  sticky-column
                >
                  <div class="d-flex">
                    <b-avatar
                      class="ml-1 mt-1"
                      badge
                      :src="getAvatar(row.avatar)"
                      :badge-variant="row.isOnline === true ? 'success' : 'secondary'"
                      badge-offset="-0.2em"
                      :text="getInitials(row.name)"
                      :size="42"
                      :variant="getRandomVariant()"
                    />
                    <div class="mt-1 font-weight-bold">
                      <span class="text-nowrap m-0 ml-1 p-0">{{ IS_MOBILE() ? FORMAT_NAME(row.name) : row.name }}</span>
                      <h5
                        v-if="activities.length > 0"
                        class="pl-1 nickname text-success mt-0"
                      >
                        {{ activities[activities.length - 1].date }}
                      </h5>
                      <h6
                        v-else
                        class="pl-1 small text-muted mt-0"
                      >
                        {{ $t('reports.never-tracked-time') }}
                      </h6>
                    </div>
                  </div>
                </b-td>
                <b-td>
                  <strong>{{ getDurationFormattedForLimitType(row, activities) }}</strong>
                  <span
                    v-if="row.projectSettings?.data?.[0]?.information?.hours && shouldShowLimit(row.projectSettings.data[0].information.type)"
                  >
                    <small> / {{ row.projectSettings.data[0].information.hours }} {{ $t('hours') }}</small>
                    <small class="d-flex justify-content-between align-items-center">
                      <span
                        v-b-tooltip.hover="`${CALC_PERCENTAGE({ total: row.projectSettings.data[0].information.hours, value: getDurationForLimitType(row, activities, false) }).toFixed(2)}% of limit reached`"
                      >
                        {{ (CALC_PERCENTAGE({
                          total: row.projectSettings.data[0].information.hours, value:
                            getDurationForLimitType(row, activities, false)
                        })).toFixed(2) }}%
                      </span>

                      <b-badge
                        v-b-tooltip.hover="`${CAPITALIZE(row.projectSettings.data[0].information.type)} Limit`"
                        class="text-capitalize cursor-default"
                        :variant="row.projectSettings.data[0].information.type === 'WEEKLY' ? 'success' : 'primary'"
                      >
                        {{ row.projectSettings.data[0].information.type === 'WEEKLY' ? 'Weekly' : 'Monthly' }}
                      </b-badge>
                    </small>
                    <b-progress
                      :max="row.projectSettings.data[0].information.hours"
                      class="time-limit-progress"
                      height="6px"
                    >
                      <b-progress-bar
                        :value="getDurationForLimitType(row, activities, false)"
                        :variant="getProgressVariant(getDurationForLimitType(row, activities, false), row.projectSettings.data[0].information.hours)"
                      />
                    </b-progress>
                    <small
                      v-if="row.projectSettings.data[0].information.type === 'MONTHLY'"
                      class="text-muted d-block mt-1"
                    >
                      {{ $t('This Week') }}: {{ getTotalHoursFormatted(activities) }}
                    </small>
                  </span>
                  <span
                    v-else-if="!row.projectSettings?.data?.[0]?.information?.hours"
                    class="d-flex"
                  >
                    <b-badge
                      variant="primary"
                      class="mt-50"
                    >
                      Unlimited
                    </b-badge>
                  </span>
                </b-td>
                <b-td
                  v-for="wa of weekArray"
                  :key="wa.key"
                  :set="workingTime = getWorkingTime(activities, wa.key)"
                  :class="{ 'today-column': isToday(wa.key) }"
                >
                  <span @click="SHOW_RECORD(wa.key, row.uuid)">
                    <b-tooltip :target="`${row.uuid}_${wa.key}`">
                      <span v-if="workingTime.rawTime > 0">
                        <feather-icon icon="ClockIcon" />
                        {{ workingTime.time }}
                        <svg
                          version="1.1"
                          viewBox="0 0 24 24"
                          xml:space="preserve"
                          xmlns="http://www.w3.org/2000/svg"
                          xmlns:xlink="http://www.w3.org/1999/xlink"
                          style="height: 14px; width: 14px; color: white;  filter: invert(100%)"
                        >
                          <g
                            id="grid_system"
                            class="st0"
                          />
                          <g id="_icons">
                            <path
                              d="M20.9,15.6C20.8,15.2,20.4,15,20,15h-4c-0.6,0-1,0.4-1,1s0.4,1,1,1h1.6l-2.3,2.3c-0.3,0.3-0.4,0.7-0.2,1.1   c0.2,0.4,0.5,0.6,0.9,0.6h4c0.6,0,1-0.4,1-1s-0.4-1-1-1h-1.6l2.3-2.3C21,16.4,21.1,16,20.9,15.6z"
                            />
                            <path
                              d="M12,2C6.5,2,2,6.5,2,12s4.5,10,10,10c0.6,0,1-0.4,1-1s-0.4-1-1-1c-4.4,0-8-3.6-8-8s3.6-8,8-8s8,3.6,8,8c0,0.6,0.4,1,1,1   s1-0.4,1-1C22,6.5,17.5,2,12,2z"
                            />
                            <path
                              d="M13,8c-0.6,0-1,0.4-1,1v2.6l-2.7,2.7c-0.4,0.4-0.4,1,0,1.4C9.5,15.9,9.7,16,10,16s0.5-0.1,0.7-0.3l3-3   c0.2-0.2,0.3-0.4,0.3-0.7V9C14,8.4,13.6,8,13,8z"
                            />
                          </g>
                        </svg>
                        {{ workingTime.idle }}
                      </span>

                    </b-tooltip>
                    <div
                      :id="`${row.uuid}_${wa.key}`"
                      class="day-hours-cell"
                    >
                      <div
                        class="day-hours-badge"
                        :class="getDayHoursBadgeClass(workingTime.rawTime)"
                      >
                        <span
                          v-if="workingTime.rawTime > 0"
                          class="hours-text"
                        >{{ formatDayHours(workingTime.rawTime)
                        }}</span>
                      </div>
                    </div>
                  </span>
                </b-td>
              </b-tr>
            </b-tbody>
            <b-tfoot v-if="!rows.length">
              <b-tr>
                <b-td
                  colspan="9"
                  class="text-center"
                >
                  {{ $t('No Data Found') }}
                </b-td>
              </b-tr>
            </b-tfoot>
          </b-table-simple>
        </b-card-body>
      </b-card>
      <div class="w-100 centralize">
        <b-pagination
          v-if="totalItems > perPage"
          v-model="currentPage"
          :total-rows="totalItems"
          :per-page="perPage"
          first-number
          last-number
          align="right"
          prev-class="prev-item"
          next-class="next-item"
          class="mt-1 mb-0"
          @input="getUsers"
        >
          <template #prev-text>
            <feather-icon
              icon="ChevronLeftIcon"
              size="18"
            />
          </template>
          <template #next-text>
            <feather-icon
              icon="ChevronRightIcon"
              size="18"
            />
          </template>
        </b-pagination>
      </div>
    </b-overlay>
  </div>
</template>
<script>

import {
  BTableSimple,
  BTr,
  BTd,
  BTh,
  BThead,
  BTbody,
  BTfoot,
  BCard,
  BCardBody,
  BAvatar,
  BProgress,
  BProgressBar,
  VBTooltip,
  BPagination,
  BTooltip,
  BOverlay,
  BBadge,
} from 'bootstrap-vue'
import useApollo from '@/plugins/graphql/useApollo'
import { setComponentReady } from '@/views/tour/tour'
import { mapActions, mapGetters } from 'vuex'
import TableHeaderSort from '@/views/common/components/TableHeaderSort.vue'
import useFilters from '@/views/common/components/Filters/useFilters'
import ActiveFilters from '@/views/common/components/Filters/ActiveFilters.vue'

const moment = require('moment-timezone')

export default {
  components: {
    BTableSimple,
    BTr,
    BTd,
    BThead,
    BTbody,
    BTfoot,
    BTh,
    BCard,
    BCardBody,
    BAvatar,
    BProgress,
    BProgressBar,
    BPagination,
    BTooltip,
    BOverlay,
    BBadge,
    TableHeaderSort,
    ActiveFilters,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  props: {
    searchDate: {
      type: String,
      required: true,
    },
    selectedTimezone: {
      type: [String, Object],
      default: null,
    },
    searchTerm: {
      type: Array,
      default: () => [],
    },
    sortOptions: {
      type: Object,
      default: () => ({
        sortBy: 'HOURS_TRACKED',
        sortDirection: 'DESC',
      }),
    },
  },
  data() {
    return {
      moment,
      rows: [],
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      showOverlay: false,
      isMounted: false,
    }
  },
  computed: {
    ...mapGetters('project', ['GET_USERS']),
    activeFilters() {
      const { getActiveFilters } = useFilters()
      return getActiveFilters.value
    },
    weekInterval() {
      const startDay = this.$store.state.project.company?.weekStartDay ?? 'SUNDAY'
      const weekStart = moment(this.searchDate).day(startDay)
      const weekEnd = moment(weekStart).add('7', 'days')
      return {
        start: weekStart,
        end: weekEnd,
      }
    },
    weekArray() {
      const startOfWeek = this.weekInterval.start
      const endOfWeek = this.weekInterval.end

      const days = []
      let day = startOfWeek

      while (day < endOfWeek) {
        days.push({
          text: day.format('ddd, MMM D'),
          key: day.format('Y-MM-DD'),
        })
        day = day.clone().add(1, 'd')
      }
      return days
    },
  },
  watch: {
    searchDate() {
      if (this.isMounted) this.getUsers()
    },
    selectedTimezone(newVal, oldVal) {
      if (this.isMounted && newVal !== oldVal) {
        this.getUsers()
      }
    },
    searchTerm: {
      handler() {
        if (this.isMounted) this.getUsers()
      },
      deep: true,
    },
    sortOptions: {
      handler() {
        if (this.isMounted) {
          this.currentPage = 1
          this.getUsers()
        }
      },
      deep: true,
    },
    activeFilters: {
      handler() {
        if (this.isMounted) {
          this.currentPage = 1
          this.getUsers()
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.getUsers()
    this.$nextTick(() => {
      this.isMounted = true
    })
  },
  methods: {
    ...mapActions('project', ['updateUsers', 'updateHasNextUsersPage']),
    getUsers() {
      const { sortBy, sortDirection } = this.sortOptions
      this.showOverlay = true
      useApollo.activities.getWeeklyTime({
        keyword: this.searchTerm && this.searchTerm.length ? this.searchTerm : null,
        page: this.currentPage,
        first: this.perPage,
        startTime: this.weekInterval.start.format('YYYY-MM-DD 00:00:00'),
        endTime: this.weekInterval.end.format('YYYY-MM-DD 00:00:00'),
        projectId: this.$store.state.project.selectedProject,
        sortBy,
        sortDirection,
        timezone: this.selectedTimezone,
        tags: this.activeFilters.tags?.map(tag => tag.name),
        ...(this.activeFilters.isOnline !== null ? { isOnline: this.activeFilters.isOnline } : {}),
      }).then(response => {
        this.rows = response.data.users.data.map(user => ({
          ...user,
        }))
        this.totalItems = response.data.users.total

        this.updateUsers(this.rows.map(({
          name, uuid, avatar, tags, isOnline,
        }) => ({
          name,
          text: name,
          value: uuid,
          avatar,
          tags,
          isOnline,
        }))).then(() => {
          if (this.GET_USERS.length === this.totalItems) this.updateHasNextUsersPage(false)
        })
      }).finally(() => {
        this.showOverlay = false
        this.$nextTick(() => {
          setComponentReady()
        })
      })
    },
    getTotalTime(duration, idle) {
      return duration * (1 - idle)
    },
    getDayRec(arr, day) {
      // const SECOND_RATIO_IN_DAY = 0.000277778
      const findDay = arr.find(d => d.date === day)
      if (findDay) {
        return {
          worked: this.getTotalTime(findDay.duration, findDay.idlePercent) / 3600,
          idle: (findDay.duration * findDay.idlePercent) / 3600,
        }
      }
      return 0
    },
    getWorkingTime(arr, day) {
      const findDay = arr.find(d => d.date === day)
      const time = findDay ? findDay.duration : 0
      return {
        time: this.CONVERT_HM(time),
        rawTime: time,
        idle: findDay ? this.CONVERT_HM(time * findDay.idlePercent) : 0,
      }
    },
    getTotalHours(arr, convertHM = true) {
      const total = arr.reduce((i, v) => i + Number(v.duration), 0)
      return convertHM ? this.CONVERT_HM(total) : total / 3600
    },
    getTotalHoursFormatted(arr) {
      const totalSeconds = arr.reduce((i, v) => i + Number(v.duration), 0)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      if (hours === 0 && minutes === 0) return '0m'
      if (hours === 0) return `${minutes}m`
      if (minutes === 0) return `${hours}h`
      return `${hours}h ${minutes}m`
    },
    getProgressVariant(value, max) {
      const percentage = (value / max) * 100
      if (percentage >= 90) return 'danger'
      if (percentage >= 70) return 'warning'
      return 'success'
    },
    formatDayHours(seconds) {
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours === 0 && minutes === 0) return ''
      if (hours === 0) return `${minutes}m`
      return `${hours}h ${minutes}m`
    },
    getDurationForLimitType(row, activities, convertHM = true) {
      const limitType = row.projectSettings?.data?.[0]?.information?.type
      if (limitType === 'MONTHLY' && this.shouldShowLimit(limitType)) {
        // Use monthlyDuration for monthly limits when in current month
        const monthlySeconds = row.monthlyDuration || 0
        return convertHM ? this.CONVERT_HM(monthlySeconds) : monthlySeconds / 3600
      }
      // Use weekly total for WEEKLY limits or when not in current month
      return this.getTotalHours(activities, convertHM)
    },
    getDurationFormattedForLimitType(row, activities) {
      const limitType = row.projectSettings?.data?.[0]?.information?.type
      if (limitType === 'MONTHLY' && this.shouldShowLimit(limitType)) {
        const totalSeconds = row.monthlyDuration || 0
        const hours = Math.floor(totalSeconds / 3600)
        const minutes = Math.floor((totalSeconds % 3600) / 60)
        if (hours === 0 && minutes === 0) return '0m'
        if (hours === 0) return `${minutes}m`
        if (minutes === 0) return `${hours}h`
        return `${hours}h ${minutes}m`
      }
      return this.getTotalHoursFormatted(activities)
    },
    getDayHoursBadgeClass(seconds) {
      if (seconds === 0) return 'no-hours'
      const hours = seconds / 3600
      if (hours < 1) return 'warning-hours'
      return 'has-hours'
    },
    isToday(date) {
      const timezone = this.selectedTimezone || 'UTC'
      const today = moment.tz(timezone).format('Y-MM-DD')
      return date === today
    },
    shouldShowLimit(limitType) {
      const now = moment()
      if (limitType === 'WEEKLY') {
        // Show for weekly limits only if the selected week includes today
        return now.isBetween(this.weekInterval.start, this.weekInterval.end, 'day', '[]')
      }
      // Show for monthly limits if the current week overlaps with the current month
      const currentMonthStart = now.clone().startOf('month')
      const currentMonthEnd = now.clone().endOf('month')

      // Check if the week interval overlaps with the current month
      return this.weekInterval.start.isSameOrBefore(currentMonthEnd, 'day')
        && this.weekInterval.end.isSameOrAfter(currentMonthStart, 'day')
    },
  },
}
</script>
<style scoped lang="scss">
.f-16 {
  font-size: 14px;
}

.progress {
  border-radius: 0 !important;

  .progress-bar {
    border-radius: 0 !important;
  }
}

.time-limit-progress {
  border-radius: 4px !important;
  background-color: rgba(115, 103, 240, 0.12);
  margin-top: 4px;

  .progress-bar {
    border-radius: 4px !important;
  }
}

.day-hours-cell {
  cursor: pointer;
}

.day-hours-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 70px;
  height: 36px;
  border-radius: 6px;
  font-weight: 500;
  font-size: 14px;

  &.has-hours {
    background-color: rgba(40, 199, 111, 0.2);
    border: 2px solid rgba(40, 199, 111, 0.5);
    color: #28c76f;

    .hours-text {
      color: #28c76f;
    }
  }

  &.warning-hours {
    background-color: rgba(255, 159, 67, 0.2);
    border: 2px solid rgba(255, 159, 67, 0.5);
    color: #ff9f43;

    .hours-text {
      color: #ff9f43;
    }
  }

  &.no-hours {
    background-color: rgba(130, 134, 139, 0.15);
    border: 2px solid rgba(130, 134, 139, 0.3);
  }
}

.dark-layout {
  .day-hours-badge {
    &.has-hours {
      background-color: rgba(40, 199, 111, 0.25);
      border-color: rgba(40, 199, 111, 0.4);
    }

    &.warning-hours {
      background-color: rgba(255, 159, 67, 0.25);
      border-color: rgba(255, 159, 67, 0.4);
    }

    &.no-hours {
      background-color: rgba(130, 134, 139, 0.2);
      border-color: rgba(130, 134, 139, 0.35);
    }
  }
}

td.today-column {
  background-color: rgba(40, 199, 111, 0.1) !important;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background-color: #28c76f;
  }
}

.dark-layout .today-column {
  background-color: rgba(40, 199, 111, 0.15) !important;
}

.fa-face-sleeping:before {
  content: "\e38d";
}

.fa-solid,
.fas {
  font-family: "Font Awesome 6 Pro";
  font-weight: 900;
}

.st0 {
  display: none;
}

.st1 {
  display: inline;
}

.st2 {
  opacity: 0.2;
  fill: none;
  stroke: #000000;
  stroke-width: 5.000000e-02;
  stroke-miterlimit: 10;
}
</style>
